import type { DashboardResponse } from "./dashboardTypes";
import { LabelledBarChart } from "./LabelledBarChart";
import { AdrLink } from "./AdrLink";
import { useCurrentRepo } from "./router";
import { useApiResource } from "./useApiResource";

// Explanations for the headline stat-grid numbers, shown as a native title=
// tooltip on hover (user-requested "rollover"). Since the 2026-08-27
// register→issues migration (see CLAUDE.md), several stats that used to key
// off the register's own frozen status_category/evidence/v0_parity columns
// were dropped outright (a frozen value can never again indicate a live
// gap) rather than kept and silently misdescribed; the two survivors that
// changed meaning (Shipped, High-criticality gaps) are redefined on Live
// signals — the issue's own open/closed state and its criticality:* label.
const STAT_HELP: Record<string, string> = {
  Remaining:
    "Capabilities whose tracking issue is still open. Read it as an upper bound: the governing ruling makes closing deliberately conservative, so open means not shipped, or shipped with a gap.",
  "Shipped at baseline":
    "Capabilities the 2026-08-27 register migration closed as already built. A classification of what existed, not delivery — 212 of them closed over three days.",
  "Closed since":
    "Capabilities closed since the migration baseline. This is delivery.",
  "Closed (30 days)": "Capabilities closed in the last 30 days. A moving window, unlike Closed since, which is cumulative.",
  "Touched (30 days)":
    "Capabilities whose issue changed at all in the last 30 days, closed or not — the difference between nothing happening and plenty happening that is not finishing. Excludes the migration's own bulk write, which touched all 435.",
  "High-criticality open":
    "Capabilities whose tracking issue is still open and carries the criticality:high label — both Live signals.",
  "Gate B remaining":
    "Of the capabilities that trace to a CAP-004 Gate B function in the CEKB blueprint, how many are still open. The rows that trace to no function at all are counted separately, in the Gate B panel.",
};

// The plane/issue-state chart now has exactly two segments — open/closed is
// the only axis that still actually changes, since the register's eight
// status_category values are frozen forever as of the 2026-08-27
// register→issues migration (see CLAUDE.md; this replaces the prior
// STATUS_COLOR/STATUS_ORDER palette that keyed on those frozen values).
const STATUS_ORDER = ["open", "closed"];
const STATUS_COLOR: Record<string, string> = {
  open: "#d98a00",
  closed: "#1f8a5b",
};
const FALLBACK_COLOR = "#9a8f7a";

function statusColor(status: string): string {
  return STATUS_COLOR[status] ?? FALLBACK_COLOR;
}

function compareStatus(a: string, b: string): number {
  const ai = STATUS_ORDER.indexOf(a);
  const bi = STATUS_ORDER.indexOf(b);
  if (ai === -1 && bi === -1) return a.localeCompare(b);
  if (ai === -1) return 1;
  if (bi === -1) return -1;
  return ai - bi;
}

function orderedStatuses(counts: Record<string, number>): string[] {
  return Object.keys(counts).sort(compareStatus);
}

// DashboardStats fetches and renders GET /api/dashboard (spec §8). No
// snapshot yet or a build failure surfaces the backend's own sanitized
// message — this component never fabricates zeroed-out stats to fill the
// gap while data is unavailable.
//
// Statistics are display-only here: spec §8 says every statistic should be
// clickable through to a filtered register view, but that view doesn't
// exist until M6 — wiring click-through is scoped to whichever milestone
// first builds the register table, not invented ahead of it here.
export function DashboardStats({ token }: { token: string }) {
  const repo = useCurrentRepo();
  const { status, data, message } = useApiResource<DashboardResponse>(token, repo, "/dashboard");

  if (status === "loading") {
    return (
      <section className="panel">
        <h2>Dashboard</h2>
        <p>Loading…</p>
      </section>
    );
  }

  if (status !== "ready") {
    return (
      <section className="panel">
        <h2>Dashboard</h2>
        <p className={status === "error" ? "err" : undefined}>{message}</p>
      </section>
    );
  }

  if (!data) return null; // unreachable once status === "ready", satisfies the type checker
  const { stats, sync, registerSync, warnings } = data;
  // Defensive against a nil-slice-marshals-to-null regression on the Go
  // side (real production incident: adrsWithNoCapabilities came back null
  // and .length on it crashed the whole tree to a blank page). The backend
  // is now fixed to always send [], but this component must not depend on
  // that holding forever.
  const priorityAttention = stats.priorityAttention ?? [];
  const adrsWithNoCapabilities = stats.adrsWithNoCapabilities ?? [];
  const mostReferencedADRs = stats.mostReferencedADRs ?? [];
  const capabilityVelocity = stats.capabilityVelocity ?? [];
  const gateBFunctions = stats.gateB.byFunction ?? [];

  return (
    <>
      <section className="panel">
        <h2>Programme statistics</h2>
        <dl className="stat-grid">
          <div title={STAT_HELP.Remaining}>
            <dt>Remaining</dt>
            <dd>
              {stats.openCapabilities} <span className="stat-denominator">of {stats.totalCapabilities}</span>
            </dd>
          </div>
          <div title={STAT_HELP["Shipped at baseline"]}>
            <dt>Shipped at baseline</dt>
            <dd>{stats.closedAtBaseline}</dd>
          </div>
          <div title={STAT_HELP["Closed since"]}>
            <dt>Closed since</dt>
            <dd>{stats.closedSinceBaseline}</dd>
          </div>
          <div title={STAT_HELP["Closed (30 days)"]}>
            <dt>Closed (30 days)</dt>
            <dd>{stats.closedLast30Days}</dd>
          </div>
          <div title={STAT_HELP["Touched (30 days)"]}>
            <dt>Touched (30 days)</dt>
            <dd>{stats.touchedLast30Days}</dd>
          </div>
          <div title={STAT_HELP["High-criticality open"]}>
            <dt>High-criticality open</dt>
            <dd>{stats.highCriticalityGaps}</dd>
          </div>
          <div title={STAT_HELP["Gate B remaining"]}>
            <dt>Gate B remaining</dt>
            <dd>
              {stats.gateB.open} <span className="stat-denominator">of {stats.gateB.total}</span>
            </dd>
          </div>
        </dl>
        <p className="sync-note">
          Docs synced from <span className="mono">{sync.ref}</span> @{" "}
          <span className="mono">{sync.sha.slice(0, 12)}</span> ({new Date(sync.fetchedAt).toLocaleString()})
        </p>
        <p className={registerSync.stale ? "sync-note err" : "sync-note"}>
          {registerSync.fetchedAt
            ? `Register last fetched ${new Date(registerSync.fetchedAt).toLocaleString()}`
            : "Register not yet fetched"}
          {registerSync.stale && " — the latest refresh failed; showing the last known-good data."}
        </p>
        {warnings && warnings.length > 0 && (
          <ul className="warnings">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel">
        <h2>Gate B traceability</h2>
        <p className="muted-note">
          Gate B belongs to CEKB-0029, the frozen Cortex Engineering Blueprint, whose capability CAP-004
          carries four functions. Each register row traces to one of them, or to none.
        </p>
        {gateBFunctions.length === 0 ? (
          <p className="muted-note">No Gate B labels on the register yet.</p>
        ) : (
          <div className="gateb-chart">
            {gateBFunctions.map((fn) => {
              const total = fn.open + fn.closed || 1;
              return (
                <div className="gateb-row" key={fn.label}>
                  <span className="gateb-label">
                    <span className="mono">{fn.label}</span>
                    {fn.name && <span className="gateb-name">{fn.name}</span>}
                  </span>
                  <div
                    className="plane-bar"
                    role="img"
                    aria-label={`${fn.label}: ${fn.open} open, ${fn.closed} closed`}
                  >
                    <span
                      className="plane-segment"
                      style={{ width: `${(fn.open / total) * 100}%`, background: statusColor("open") }}
                      title={`open: ${fn.open}`}
                    />
                    <span
                      className="plane-segment"
                      style={{ width: `${(fn.closed / total) * 100}%`, background: statusColor("closed") }}
                      title={`closed: ${fn.closed}`}
                    />
                  </div>
                  <span className="gateb-count">{fn.open} open</span>
                </div>
              );
            })}
          </div>
        )}
        <p className="muted-note">
          <strong>{stats.gateB.untraced}</strong> of {stats.totalCapabilities} rows trace to no Gate B
          function. The register records that as its own finding rather than as a backlog: the blueprint's
          four functions under-represent Atlas, and the pack forbids inventing new ones, so closing the gap
          is a governance act.
        </p>
      </section>

      <section className="panel">
        <h2>Capability velocity</h2>
        <p className="muted-note">
          Capabilities whose tracking issue was closed, per week. The series starts after the 2026-08-27
          register migration, which closed 212 capabilities as bookkeeping rather than as delivery.
        </p>
        {capabilityVelocity.length === 0 ? (
          <p className="muted-note">No weeks to chart yet.</p>
        ) : (
          <LabelledBarChart
            data={capabilityVelocity.map((w) => ({ label: w.period, count: w.count }))}
            ariaLabel="Capabilities closed per week"
          />
        )}
      </section>

      <section className="panel">
        <h2>Delivery posture by plane</h2>
        <div className="plane-chart">
          {Object.entries(stats.byPlane).map(([plane, counts]) => {
            const total = Object.values(counts).reduce((a, b) => a + b, 0);
            return (
              <div className="plane-row" key={plane}>
                <span className="plane-label">{plane}</span>
                <div className="plane-bar" role="img" aria-label={`${plane}: ${total} capabilities`}>
                  {orderedStatuses(counts).map((statusName) => {
                    const count = counts[statusName];
                    const pct = total === 0 ? 0 : (count / total) * 100;
                    return (
                      <span
                        key={statusName}
                        className="plane-segment"
                        title={`${statusName}: ${count}`}
                        style={{ width: `${pct}%`, background: statusColor(statusName) }}
                      />
                    );
                  })}
                </div>
                <span className="plane-total">{total}</span>
              </div>
            );
          })}
        </div>
        <ul className="chart-legend">
          {Array.from(new Set(Object.values(stats.byPlane).flatMap((c) => Object.keys(c))))
            .sort(compareStatus)
            .map((statusName) => (
              <li key={statusName}>
                <span className="swatch" style={{ background: statusColor(statusName) }} />
                {statusName}
              </li>
            ))}
        </ul>
      </section>

      {priorityAttention.length > 0 && (
        <section className="panel">
          <h2>Priority attention</h2>
          <ul className="priority-list">
            {priorityAttention.slice(0, 10).map((item) => (
              <li key={item.capabilityId}>
                <div className="priority-head">
                  <span className="mono">{item.capabilityId}</span>
                  <span className="priority-name">{item.capabilityName}</span>
                  <span
                    className="priority-score"
                    title="One point each for: high criticality, issue still open, missing governing ADR — see reasons below. Phase and future surface no longer score: both are frozen 2026-08-27 snapshot cells that cannot move again."
                  >
                    {item.score}
                  </span>
                </div>
                <p className="priority-reasons">{item.reasons.join(" · ")}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="panel">
        <h2>ADR coverage</h2>
        <div className="adr-coverage">
          <div>
            <h3>Most referenced</h3>
            <ul className="adr-list">
              {mostReferencedADRs.slice(0, 8).map((a) => (
                <li key={a.adrId}>
                  <AdrLink id={a.adrId} />
                  <span>{a.count}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3>No capability references</h3>
            {adrsWithNoCapabilities.length === 0 ? (
              <p className="muted-note">None — every ADR is cited by at least one capability.</p>
            ) : (
              <ul className="adr-list">
                {adrsWithNoCapabilities.map((id) => (
                  <li key={id}>
                    <AdrLink id={id} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
